import { DatosDePlan } from '../../hooks/useDatosDePlanes';
import { CONTRIBUTION_LIMITS } from '../contributionLimits';
import { AportacionMensual, aportacionMensual } from './aportacion';
import { ResumenDeMovimientos, resumenDeMovimientos } from './movimientos';
import {
	ContributionStatusEnum,
	SingleUserContributionData
} from '../../api/users/interfaces/ContributionData';
import { PlanValuePointI } from '../../api/users/getPlansValueHistory';
import {
	DESDE_EL_INICIO,
	PeriodoEvolucion,
	rentabilidadAlCierreDelAnio,
	valorAlCierreDelAnio
} from './evolucion';
import { anioDeMadrid } from './formato';
import { ResumenPlanVM, TipoPlanResumen } from './planes';
import {
	Rentabilidad,
	rentabilidadAgregada,
	rentabilidadDelAnio,
	rentabilidadDelPlan
} from './rentabilidad';

/** Una card del Resumen con lo que se pide por plan (Fase A2). `null` = sin dato. */
export interface PlanConDatos extends ResumenPlanVM {
	cargandoDatos: boolean;
	/** Acumulada, desde el inicio: la de las cifras grandes del Resumen. */
	rentabilidad: Rentabilidad | null;
	/** La del año en curso: la de la card («Rentabilidad 2026», como en Proa). */
	rentabilidadAnio: Rentabilidad | null;
	aportacion: AportacionMensual | null;
	movimientos: ResumenDeMovimientos | null;
}

export interface ContextoDatos {
	hoy: Date;
	salario: number | null | undefined;
	/** €/mes de la empresa para ESTE plan: lo de `current-user` en el empleo vigente, 0 en el resto. */
	empresa: number | null | undefined;
}

/**
 * Junta la card (`buildResumenPlans`) con lo que llegó de su plan (`useDatosDePlanes`).
 *
 * 🔴 Sin movimientos no hay rentabilidad: el coste de los traspasos sale de ellos, y sin él un
 * traspaso se leería como ganancia.
 */
export function conDatos(
	plan: ResumenPlanVM,
	datos: DatosDePlan | undefined,
	{ hoy, salario, empresa }: ContextoDatos
): PlanConDatos {
	if (!datos || plan.estado === 'enProceso') {
		return {
			...plan,
			cargandoDatos: false,
			rentabilidad: null,
			rentabilidadAnio: null,
			aportacion: null,
			movimientos: null
		};
	}
	const movimientos = datos.movimientos
		? resumenDeMovimientos(datos.movimientos, {
				tipo: plan.tipo,
				hoy,
				flujoDesde: plan.saldoInicioAnioFecha
			})
		: null;
	return {
		...plan,
		cargandoDatos: datos.cargando,
		rentabilidad:
			datos.adhesion && movimientos
				? rentabilidadDelPlan({
						tipo: plan.tipo,
						adhesionDetails: datos.adhesion,
						costeTraspasos: movimientos.costeTraspasos,
						rescatadoTotal: movimientos.rescatadoTotal
					})
				: null,
		rentabilidadAnio:
			datos.adhesion && movimientos
				? rentabilidadDelAnio({
						adhesionDetails: datos.adhesion,
						saldoInicioAnio: plan.saldoInicioAnioEuros,
						flujoNetoAnio: movimientos.flujoNetoAnio
					})
				: null,
		aportacion: aportacionMensual({ tipo: plan.tipo, config: datos.config, salario, empresa }),
		movimientos
	};
}

/** Los planes que cuentan en totales, filtro y barra: el plan en proceso aún no tiene cifras. */
const filtrables = (planes: PlanConDatos[]) => planes.filter((plan) => plan.estado !== 'enProceso');

/**
 * Lo de UN plan en el periodo del selector de año del Resumen, que afecta a todo lo que hay debajo
 * de los selectores salvo a las cards (Marisa, 21/10/2026).
 */
export interface CifrasDelPeriodo {
	/** El plan ya existía ese año. Si no, ese año no cuenta en nada. */
	existe: boolean;
	/** Total ahorrado: hoy; en un año pasado, lo que valía al cerrarlo. `null` = sin dato. */
	saldo: number | null;
	/** Desde el inicio, la acumulada; un año, la de ese año. */
	rentabilidad: Rentabilidad | null;
	/** Aportado y rescatado en el año del periodo (desde el inicio: el año en curso). */
	aportado: number;
	rescatado: number;
	/** Llegaron los movimientos del plan: sin ellos, aportado y rescatado no son reales. */
	conMovimientos: boolean;
	/**
	 * 🔴 El plan está VACÍO: saldo 0 € y ningún movimiento confirmado, como el recién abierto que
	 * espera su primer traspaso. No es «sin dato» —no le falta nada, aún no tiene nada—, así que no
	 * cuenta en la rentabilidad agregada (product#58). Sin saber su saldo o sus movimientos, no se da
	 * por vacío.
	 */
	vacio: boolean;
}

/** Saldo 0 y ningún movimiento confirmado. Lo desconocido (`null`) nunca es vacío. */
const estaVacio = (saldo: number | null, movimientos: ResumenDeMovimientos | null): boolean =>
	saldo === 0 && movimientos?.confirmados === 0;

/**
 * Lo mismo al cierre de un año PASADO: ningún movimiento confirmado hasta ese año, aunque después sí
 * los haya (abierto en diciembre y con su primer traspaso en enero). Sin foto ese año (`saldo` null)
 * también: sin ningún movimiento hasta entonces, no podía tener nada. Sin movimientos, no se sabe.
 */
const estabaVacio = (
	saldo: number | null,
	movimientos: SingleUserContributionData[] | null,
	anio: string
): boolean =>
	movimientos !== null &&
	(saldo === null || saldo === 0) &&
	!movimientos.some(
		(m) =>
			m.statusContribution === ContributionStatusEnum.CONFIRMED &&
			m.dateContribution.slice(0, 4) <= anio
	);

/**
 * Las cifras de un plan en el periodo: desde el inicio y el año en curso salen de la card (lo de
 * hoy, en Caser); un año pasado, de la serie mensual (lo que valía al cerrarlo) y de los
 * movimientos de ese año.
 */
export function cifrasDelPeriodo(
	plan: PlanConDatos,
	{
		periodo,
		hoy,
		movimientos,
		puntos,
		creadoEn
	}: {
		periodo: PeriodoEvolucion;
		hoy: Date;
		/** Los movimientos crudos del plan (`useDatosDePlanes`). */
		movimientos: SingleUserContributionData[] | null;
		/** La serie mensual del plan (`GET /users/plans/value-history`). */
		puntos: PlanValuePointI[];
		creadoEn: string;
	}
): CifrasDelPeriodo {
	const anioEnCurso = String(anioDeMadrid(hoy));
	if (periodo === DESDE_EL_INICIO || periodo >= anioEnCurso) {
		return {
			existe: true,
			saldo: plan.saldoEuros,
			rentabilidad: periodo === DESDE_EL_INICIO ? plan.rentabilidad : plan.rentabilidadAnio,
			aportado: plan.movimientos?.aportadoAnio ?? 0,
			rescatado: plan.movimientos?.rescatadoAnio ?? 0,
			conMovimientos: plan.movimientos !== null,
			vacio: estaVacio(plan.saldoEuros, plan.movimientos)
		};
	}
	const delAnio = movimientos
		? resumenDeMovimientos(movimientos, { tipo: plan.tipo, hoy, anio: periodo })
		: null;
	const saldo = valorAlCierreDelAnio(puntos, periodo);
	return {
		existe: String(anioDeMadrid(new Date(creadoEn))) <= periodo,
		saldo,
		rentabilidad: movimientos
			? rentabilidadAlCierreDelAnio({ puntos, anio: periodo, movimientos, creadoEn, hoy })
			: null,
		aportado: delAnio?.aportadoAnio ?? 0,
		rescatado: delAnio?.rescatadoAnio ?? 0,
		conMovimientos: delAnio !== null,
		vacio: estabaVacio(saldo, movimientos, periodo)
	};
}

export interface ResumenDelFiltro {
	numPlanes: number;
	/** Los saldos sin dato quedan fuera: nunca cuentan como 0. */
	totalAhorrado: number;
	rentabilidad: Rentabilidad | null;
	rescatadoAnio: number;
	/** Algún plan elegido aún está cargando: la rentabilidad todavía no se afirma. */
	cargando: boolean;
}

/** Las cifras de un plan: las del periodo si las hay; si no, las de la card (desde el inicio). */
const cifrasDe = (
	plan: PlanConDatos,
	cifras?: Record<string, CifrasDelPeriodo>
): CifrasDelPeriodo =>
	cifras?.[plan.id] ?? {
		existe: true,
		saldo: plan.saldoEuros,
		rentabilidad: plan.rentabilidad,
		aportado: plan.movimientos?.aportadoAnio ?? 0,
		rescatado: plan.movimientos?.rescatadoAnio ?? 0,
		conMovimientos: plan.movimientos !== null,
		vacio: estaVacio(plan.saldoEuros, plan.movimientos)
	};

/**
 * Las cifras grandes del Resumen, de los planes elegidos en el filtro y, con `cifras`, del periodo
 * del selector de año (un plan que aún no existía ese año no cuenta).
 */
export function resumenDelFiltro(
	planes: PlanConDatos[],
	seleccion: string[],
	cifras?: Record<string, CifrasDelPeriodo>
): ResumenDelFiltro {
	const elegidos = filtrables(planes).filter(
		(plan) => seleccion.includes(plan.id) && cifrasDe(plan, cifras).existe
	);
	const cargando = elegidos.some((plan) => plan.cargandoDatos);
	return {
		numPlanes: elegidos.length,
		totalAhorrado: elegidos.reduce((total, plan) => total + (cifrasDe(plan, cifras).saldo ?? 0), 0),
		rentabilidad: cargando
			? null
			: rentabilidadAgregada(
					// Un plan vacío no tiene rentabilidad que aportar: fuera, no «sin dato» (product#58).
					elegidos
						.map((plan) => cifrasDe(plan, cifras))
						.filter((delPlan) => !delPlan.vacio)
						.map((delPlan) => delPlan.rentabilidad)
				),
		rescatadoAnio: elegidos.reduce((total, plan) => total + cifrasDe(plan, cifras).rescatado, 0),
		cargando
	};
}

/**
 * El tope anual de aportación de la persona, el mismo que enseña Inicio
 * (`ContributionLimitsChartV2`): es por PERSONA, así que dos planes individuales comparten los
 * 1.500 €. Con un plan de autónomo, el tope reducido aunque también tenga empleo (como Inicio con
 * `isFreelance`).
 */
export function topeAnual(tipos: TipoPlanResumen[]): number {
	if (tipos.includes('AUTONOMO')) return CONTRIBUTION_LIMITS.GLOBAL_FREELANCE;
	if (tipos.includes('EMPLEO')) return CONTRIBUTION_LIMITS.GLOBAL_EMPLOYEE;
	return CONTRIBUTION_LIMITS.GLOBAL_INDIVIDUAL;
}

export interface BarraDelAnio {
	modo: 'aportaciones' | 'rescates';
	items: { planId: string; nombre: string | null; importe: number }[];
	total: number;
	/** Aportaciones: el tope anual. Rescates: lo ahorrado (como Proa). */
	tope: number;
	/** De algún plan no llegaron (o aún no) los movimientos: el total puede quedarse corto. */
	completa: boolean;
}

/**
 * La barra del año: aportaciones por plan contra el tope, o rescates si ese año hubo alguno. Con
 * `seleccion` y `cifras`, solo de los planes del filtro y del año del selector (los dos selectores
 * afectan a todo lo de debajo, Marisa, 21/10/2026). El tope sigue siendo el de la PERSONA: sale
 * de todos sus planes, no de los elegidos.
 */
export function barraDelAnio(
	planes: PlanConDatos[],
	{ seleccion, cifras }: { seleccion?: string[]; cifras?: Record<string, CifrasDelPeriodo> } = {}
): BarraDelAnio {
	const todos = filtrables(planes);
	const elegidos = todos.filter(
		(plan) => (!seleccion || seleccion.includes(plan.id)) && cifrasDe(plan, cifras).existe
	);
	const rescates = elegidos.some((plan) => cifrasDe(plan, cifras).rescatado > 0);
	const importeDe = (plan: PlanConDatos) =>
		rescates ? cifrasDe(plan, cifras).rescatado : cifrasDe(plan, cifras).aportado;
	const items = elegidos
		.map((plan) => ({ planId: plan.id, nombre: plan.nombre, importe: importeDe(plan) }))
		.filter((item) => item.importe > 0);
	return {
		modo: rescates ? 'rescates' : 'aportaciones',
		items,
		total: items.reduce((total, item) => total + item.importe, 0),
		tope: rescates
			? elegidos.reduce((total, plan) => total + (cifrasDe(plan, cifras).saldo ?? 0), 0)
			: topeAnual(todos.map((plan) => plan.tipo)),
		completa: elegidos.every((plan) => !plan.cargandoDatos && cifrasDe(plan, cifras).conMovimientos)
	};
}
